import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { ConnectionStatus, Network } from '@capacitor/network';

interface NetworkContextProps {
  connected: boolean;
  loading: boolean;
}

const NetworkContext = createContext<NetworkContextProps | undefined>(
  undefined
);

export const NetworkProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [connected, setConnected] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const onNetworkChange = useCallback((status: ConnectionStatus) => {
    setConnected((prev) => {
      if (status.connected !== prev) {
        return status.connected;
      }
      return prev;
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    Network.getStatus()
      .then((status) => {
        setConnected((prev) => {
          if (status.connected !== prev) {
            return status.connected;
          }
          return prev;
        });
        setLoading(false);
      })
      .catch(() => {
        setConnected(false);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    Network.addListener('networkStatusChange', onNetworkChange);

    return () => {
      Network.removeAllListeners();
    };
  }, []);

  return (
    <NetworkContext.Provider value={{ connected: connected, loading }}>
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetwork = (): NetworkContextProps => {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
};
